import React from "react";
import styled from "styled-components";
import { colors, radius } from "../theme";
import { AppScroll, AppEyebrow, AppTitle, Chip, ChipRow } from "./shared";

interface Role {
  company: string;
  title: string;
  location: string;
  period: string;
  accent: string;
  bullets: string[];
  skills: string[];
}

const roles: Role[] = [
  {
    company: "Reflexivity",
    title: "Frontend Developer",
    location: "New York, NY",
    period: "May 2024 - Present",
    accent: colors.green,
    bullets: [
      "Led delivery of major React/TypeScript product initiatives including automated Daily Briefs, real-time alerts and notifications, PostHog analytics tracking, earnings insights, document search, onboarding flows, and theme-management experiences.",
      "Owned Daily Briefs end-to-end as primary engineer across scoping, ticketing, backend/frontend implementation, and launch; it is being adopted as the basis for a new landing-page experience.",
      "Built and managed product-wide PostHog analytics tracking, instrumenting core app interactions to measure user behavior, feature adoption, and engagement while informing business-critical decisions.",
      "Drove cross-functional delivery with Product, Design, Backend, Data, Knowledge Graph, and AI teams, translating ambiguous requirements into scoped tickets, API contracts, reusable components, QA plans, analytics events, and production releases.",
      "Improved frontend architecture by migrating 30+ design-system components, consolidating duplicated UI patterns, removing legacy dependencies, and expanding end-to-end (E2E) test coverage.",
    ],
    skills: [
      "React",
      "TypeScript",
      "PostHog",
      "Zustand",
      "TanStack",
      "Vite",
      "Storybook",
      "SCSS Modules",
      "Zod",
      "GraphQL",
      "REST",
      "Playwright",
      "Jest",
      "Grafana",
      "New Relic",
      "Git",
    ],
  },
  {
    company: "Atom Finance",
    title: "Full Stack Developer",
    location: "New York, NY",
    period: "May 2021 - May 2024",
    accent: colors.sky,
    bullets: [
      "Led frontend development of major B2C web and mobile product features, including Financial Insights, ETF/mutual fund research pages, real-time price displays, embeddable data-visualization widgets, and a complete ChartIQ-based advanced charting overhaul.",
      "Built business-critical market-data systems spanning vendor integrations, real-time ingestion services, WebSocket price APIs, SQS/Redis streaming pipelines, and market-data entitlements.",
      "Defined and built a mobile data visualizations component library for iOS from the ground-up, using Swift and Core Graphics, and implemented features such as authentication, integration, extensive global customization options, and a real-time data streaming WebSocket client.",
    ],
    skills: [
      "React",
      "TypeScript",
      "JavaScript",
      "Swift",
      "Core Graphics",
      "ChartIQ",
      "WebSockets",
      "Redis",
      "SQS",
      "Kafka",
      "GraphQL",
      "REST",
      "Postgres",
      "TypeORM",
      "Node",
      "Express",
      "Docker",
      "Postman",
      "Git",
    ],
  },
  {
    company: "Reelgood",
    title: "iOS Intern",
    location: "San Francisco, CA",
    period: "Jul 2020 - Feb 2021",
    accent: colors.peach,
    bullets: [
      "Automated UI testing and screenshot generation of the iOS application to improve the efficiency of the existing QA process.",
      "Contributed to a refactor of the iOS application that replaced all Storyboard and XIB files with fully programmatically-created UI components following the MVVM pattern, improving code reusability and testing capabilities.",
      "Led the development of a complete rewrite of the tvOS application to support later API versions and the latest UI/UX standards, built using programmatic UI, MVVM, and RxSwift.",
    ],
    skills: ["Swift", "UIKit", "RxSwift", "MVVM", "Programmatic UI", "iOS", "tvOS"],
  },
];

const Card = styled.div<{ $accent: string }>`
  border: 2px solid ${colors.ink};
  border-radius: ${radius.md};
  background: #fff;
  padding: 18px 20px 20px;
  margin-bottom: 18px;
  box-shadow: 4px 4px 0 rgba(29, 29, 31, 0.12);
  border-left: 8px solid ${({ $accent }) => $accent};
`;

const CardHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: 4px 16px;
`;

const Company = styled.h3`
  margin: 0;
  font-size: 19px;
  font-weight: 700;
`;

const Period = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.inkFaint};
`;

const RoleLine = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.inkSoft};
  margin: 2px 0 14px;
`;

const Bullets = styled.ul`
  margin: 0 0 16px;
  padding-left: 20px;
  li {
    font-size: 14px;
    line-height: 1.55;
    color: ${colors.inkSoft};
    margin-bottom: 8px;
  }
`;

const ExperienceApp = () => (
  <AppScroll>
    <AppEyebrow>Where I've worked</AppEyebrow>
    <AppTitle>Experience</AppTitle>
    <div style={{ marginTop: 18 }}>
      {roles.map((role) => (
        <Card key={role.company} $accent={role.accent}>
          <CardHead>
            <Company>{role.company}</Company>
            <Period>{role.period}</Period>
          </CardHead>
          <RoleLine>
            {role.title} &middot; {role.location}
          </RoleLine>
          <Bullets>
            {role.bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </Bullets>
          <ChipRow>
            {role.skills.map((s) => (
              <Chip key={s} $bg={role.accent}>
                {s}
              </Chip>
            ))}
          </ChipRow>
        </Card>
      ))}
    </div>
  </AppScroll>
);

export default ExperienceApp;
